import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import "./coober-pedy.css";

/* Vadoo.tv embedded videos — carried over from the legacy CooberPedyPage. */
const VADOO_VIDEOS = [
  {
    id: "tLdL0W0Qze8c2KwayjRxem5CxuyACsVc",
    containerId: "video-1",
    title: "Mining with George and Yannie",
    text: "Follow local miners George and Yannie as they search for precious opals in the fields of Coober Pedy",
  },
  {
    id: "8m3Tf3igiKbJtptY3xxPTYSAHHY9X80A",
    containerId: "video-2",
    title: "Noodling with Veronika",
    text: "Join Veronika as she demonstrates the traditional art of noodling - searching through mining mullock for opals",
  },
  {
    id: "663tHYBXD5tLX7cPT2qk7AyHI5TTWSm6",
    containerId: "video-3",
    title: "The Bomb King",
    text: 'Meet the legendary "Bomb King" and learn about explosive mining techniques in Coober Pedy\'s history',
  },
];

const STATS = [
  {
    kicker: "RESIDENTS",
    kickerClass: "",
    value: "2,500+",
    text: "A remarkable community living in the heart of the desert.",
  },
  {
    kicker: "DUGOUTS",
    kickerClass: "cp-kicker--teal",
    value: "1,500+",
    text: "Underground homes, naturally cool at 22–24°C year-round.",
  },
  {
    kicker: "NATIONALITIES",
    kickerClass: "cp-kicker--turquoise",
    value: "45+",
    text: "A truly multicultural town built by opal miners from across the globe.",
  },
];

const GALLERY = [
  "/design/assets/cp-2.jpg",
  "/design/assets/cp-3.jpg",
  "/design/assets/cp-4.jpg",
  "/design/assets/cp-5.jpg",
  "/design/assets/tour-4.jpg",
  "/design/assets/cp-1.jpg",
];

const SPIRIT_CARDS = [
  {
    kicker: "BUILDING A TOWN",
    kickerClass: "",
    title: "Hand-dug beginnings",
    text: "From the 1920s onward, miners worked by hand in heat that often exceeded 50°C — with picks, shovels and unwavering determination.",
  },
  {
    kicker: "UNDERGROUND INNOVATION",
    kickerClass: "cp-kicker--teal",
    title: "Dugout dwellings",
    text: "To escape the heat, miners carved homes into the hillsides — naturally cool at 22–24°C, allowing families to thrive.",
  },
  {
    kicker: "A GLOBAL COMMUNITY",
    kickerClass: "cp-kicker--turquoise",
    title: "45+ countries",
    text: "Greeks, Italians, Yugoslavians, Germans and people from across the globe worked side by side in the opal fields.",
  },
  {
    kicker: "THE LEGACY LIVES ON",
    kickerClass: "cp-kicker--blue",
    title: "Mining families today",
    text: "Families like those at Umoona carry the legacy forward — sharing stories, knowledge and a century of heritage.",
  },
];

const CLIMATE_CARDS = [
  {
    kicker: "SUMMER · DEC–FEB",
    kickerClass: "",
    title: "Up to 50°C",
    text: "Surface temperatures make underground living essential for comfort.",
  },
  {
    kicker: "YEAR-ROUND",
    kickerClass: "cp-kicker--turquoise",
    title: "A steady 22–24°C",
    text: "Underground homes stay comfortable without heating or cooling — with rainfall averaging only 175mm a year.",
  },
];

export default function RCooberPedyPage() {
  // Load Vadoo player scripts for embedded videos (behavior preserved from
  // the legacy CooberPedyPage: sequential load, cleanup on unmount).
  useEffect(() => {
    const scriptsLoaded = [];

    VADOO_VIDEOS.forEach((video) => {
      const script = document.createElement("script");
      script.src = "https://api.vadoo.tv/static/vadoo_player.min.js";
      script.id = `player_script_${video.containerId}`;
      script.setAttribute("vid", video.id);
      script.async = false; // Load sequentially

      script.onload = () => {
        console.log(`Vadoo player script loaded for ${video.containerId}`);
      };

      document.body.appendChild(script);
      scriptsLoaded.push(script);
    });

    return () => {
      // Cleanup scripts on unmount
      scriptsLoaded.forEach((script) => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  return (
    <MarketingShell pageClass="pg-coober-pedy">
      <SEOHead
        title="Coober Pedy - Australia's Underground Town | Umoona Opal Mine"
        description="Discover Coober Pedy, the world's opal capital. Learn about the history, underground living, and unique lifestyle of this remarkable outback town."
        keywords="Coober Pedy, underground town, opal capital, outback Australia, dugout homes, South Australia"
        ogImage="/og-umoona.jpg"
        canonical="/coober-pedy"
      />

      {/* HERO */}
      <div className="u-hero">
        <img
          className="u-hero__img"
          src="/design/assets/cp-1.jpg"
          alt="Coober Pedy - Australia's Underground Town"
        />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">World's opal capital</div>
          <h1 className="u-hero__title">
            Coober Pedy — Australia's Underground Town
          </h1>
          <p className="u-hero__sub">
            Experience the world's opal capital and one of the most unique
            towns on Earth. Underground living, stunning desert landscapes, and
            a multicultural community thriving in the outback.
          </p>
        </div>
      </div>

      {/* STATS + INTRO */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-grid3 cp-stats">
          {STATS.map((stat) => (
            <div className="cp-card" key={stat.kicker}>
              <div className={`cp-kicker ${stat.kickerClass}`.trim()}>
                {stat.kicker}
              </div>
              <div className="cp-card__title">{stat.value}</div>
              <p className="cp-card__text">{stat.text}</p>
            </div>
          ))}
        </div>
        <div className="u-eyebrow">Welcome to the opal capital of the world</div>
        <h2 className="u-h2">
          Coober Pedy is like nowhere else on Earth. Located 850 kilometres
          north of Adelaide in South Australia's desert, it is famous for
          underground living and world-class opals. The name comes from the
          Aboriginal term "kupa-piti" — "white man's hole" — a reference to the
          dugout homes that make this town so unique.
        </h2>
      </section>

      {/* GALLERY */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Life in the opal fields</div>
        <h2 className="u-h2">Gallery</h2>
        <div className="u-grid3 cp-gallery">
          {GALLERY.map((src) => (
            <div className="cp-photo" key={src}>
              <img src={src} alt="Scenes of life in the Coober Pedy opal fields, South Australia" loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
      </section>

      {/* VIDEOS — Vadoo embeds carried over from the legacy page */}
      <section className="u-sectpad u-sect-test cp-videos">
        <h2 className="u-h2">Watch: Life in Coober Pedy</h2>
        <p className="cp-videos__sub">
          Experience authentic stories from local miners and residents who call
          Coober Pedy home
        </p>
        <div className="u-grid3">
          {VADOO_VIDEOS.map((video) => (
            <div className="cp-videocard" key={video.containerId}>
              <div className="cp-videocard__frame">
                <div
                  id={video.containerId}
                  className="vadoo_player"
                  vid={video.id}
                  etype="responsive"
                  style={{ width: "100%", height: "100%" }}
                ></div>
              </div>
              <div className="cp-videocard__body">
                <div className="cp-card__title">{video.title}</div>
                <p className="cp-card__text">{video.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* THE SPIRIT OF COOBER PEDY'S OPAL MINERS */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">The spirit of Coober Pedy's opal miners</div>
        <h2 className="u-h2">
          A story of determination, resilience and the pioneering spirit that
          transformed a harsh desert into a thriving community.
        </h2>
        <div className="u-grid2">
          {SPIRIT_CARDS.map((card) => (
            <div className="cp-card" key={card.kicker}>
              <div className={`cp-kicker ${card.kickerClass}`.trim()}>
                {card.kicker}
              </div>
              <div className="cp-card__title">{card.title}</div>
              <p className="cp-card__text">{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EXTREME CLIMATE */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Extreme climate</div>
        <h2 className="u-h2">One of the harshest environments in Australia</h2>
        <div className="u-grid2">
          {CLIMATE_CARDS.map((card) => (
            <div className="cp-card" key={card.kicker}>
              <div className={`cp-kicker ${card.kickerClass}`.trim()}>
                {card.kicker}
              </div>
              <div className="cp-card__title">{card.title}</div>
              <p className="cp-card__text">{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">
            Experience Coober Pedy for yourself
          </h2>
          <p className="u-ctacenter__sub">
            Descend into the underground and discover why this desert town is
            unlike anywhere else.
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/underground-tours" className="u-btn u-btn--primary">
              Book Underground Tour
            </Link>
            <Link to="/free-museum" className="u-btn u-btn--ghost">
              Visit Free Museum
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
